import { Link as LinkIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";

import { GUIDE_ARTICLES } from "./articles";

interface GuideArticleProps {
  slug: string;
  title: string;
  intro?: ReactNode;
  children: ReactNode;
}

export function GuideArticle({ slug, title, intro, children }: GuideArticleProps) {
  return (
    <div className="mx-auto flex w-full max-w-6xl gap-8 p-6">
      <GuideNav currentSlug={slug} />
      <article className="flex-1 space-y-8">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-text-faint">
            <Link to="/help" className="hover:text-accent">
              Guide
            </Link>
          </p>
          <h1 className="text-2xl font-semibold text-text-primary">{title}</h1>
          {intro ? <div className="text-sm text-text-secondary">{intro}</div> : null}
        </header>
        {children}
      </article>
    </div>
  );
}

interface SectionProps {
  id?: string;
  title: string;
  children: ReactNode;
}

export function Section({ id, title, children }: SectionProps) {
  const headingId = id;
  return (
    <section className="group space-y-3 scroll-mt-20" id={headingId}>
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-medium text-text-primary">{title}</h2>
        {headingId ? (
          <a
            href={`#${headingId}`}
            aria-label={`Link to section: ${title}`}
            className="opacity-0 transition-opacity group-hover:opacity-100 text-text-faint hover:text-accent"
          >
            <LinkIcon size={14} aria-hidden="true" />
          </a>
        ) : null}
      </div>
      <div className="space-y-3 text-sm leading-relaxed text-text-secondary">{children}</div>
    </section>
  );
}

interface GuideNavProps {
  currentSlug: string;
}

export function GuideNav({ currentSlug }: GuideNavProps) {
  return (
    <nav className="hidden w-56 shrink-0 lg:block" aria-label="Guide articles">
      <p className="px-2 pb-2 text-xs uppercase tracking-wide text-text-faint">Guide</p>
      <ul className="space-y-1 text-sm">
        {GUIDE_ARTICLES.map((article) => {
          const isCurrent = article.slug === currentSlug;
          return (
            <li key={article.slug}>
              <NavLink
                to={`/help/${article.slug}`}
                className={
                  isCurrent
                    ? "block rounded-md px-2 py-1.5 bg-card-hover font-medium text-text-primary"
                    : "block rounded-md px-2 py-1.5 text-text-secondary hover:bg-card-hover hover:text-text-primary"
                }
              >
                {article.title}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
